/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component, OnInit } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { LanguageService, LogService, UtilsService } from '../../../../../core/services';
import { ViewLogsApiService } from '../../../shared/http';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-logs',
    templateUrl: './logs.component.html',
    styleUrls: ['./logs.component.scss']
})

export class LogsComponent extends BaseComponent implements OnInit {
    /**
     * To store the form data.
     */
    public logsForm: FormGroup;

    /**
     * To store the log data string arrays.
     */
    public tableRows: any[] = [];

    /**
     * A boolean to determine if the config options should be open or closed.
     */
    public configOpen: boolean = true;

    /**
     * A boolean to determine if the next button should be active or not.
     */
    public noMoreLogs: boolean = false;

    /**
     * Hold toggle state.
     */
    public selectAllToggle: boolean = true;

    /**
     * A boolean to determine if the clear all button should be active or not.
     */
    public clearAllActive: boolean = false;

    /**
     * To store the active services to be able to check the length to enable and disable buttons.
     */
    public activeServices: string[] = [];

    /**
     * To store the date and time of the first log.
     */
    public firstLog: any = '';

    /**
     * To store the min date available for the logs.
     */
    public minDate: string = '';

    /**
     * To store the max date available for the logs.
     */
    public maxDate: string = '';

    /**
     * Loading completed flag.
     */
    public logsLoaded: boolean = false;

    /**
     * Loaded with error flag.
     */
    public errorLoaded: boolean = false;

    /**
     * Store available services.
     */
    public availableServices: any[] = [];

    /**
     * To store the page lookup for this component.
     * @private
     */
    private pageLookUp: any = {};

    constructor(
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private logService: LogService,
        private utilsService: UtilsService,
        private viewLogsApiService: ViewLogsApiService,
    ) {
        super();
    }

    /**
     * Get form services array.
     */
    get servicesArray(): FormArray {
        return this.logsForm.get('services') as FormArray;
    }

    ngOnInit() {
        this.setUpPageData();
    }

    /**
     * A check to determine if the button is disabled or not.
     * @param buttonId
     */
    public isButtonDisabled(buttonId: string): boolean {
        return this.pageLookUp[buttonId] && this.pageLookUp[buttonId].isDisabled;
    }

    /**
     * Toggle the selected services.
     * @param active
     */
    public toggleSelectedServices(active: boolean): void {
        for (const control of this.servicesArray.controls) {
            control['controls']['selected'].setValue(active);
        }

        this.activeServices = this.formatServices(this.logsForm.get('services').value);
        this.enableDisableSelectAll();
    }

    /**
     * Get the log data.
     * @param optionsData
     */
    public getLogs(optionsData?: any): void {
        // Get the form data.
        const options = optionsData ? optionsData : this.getFormattedFormData();

        this.viewLogsApiService.getLogData(options, { timeout: 30000 })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.firstLog = new Date(data.first);
                    this.noMoreLogs = false;
                    this.checkLogLength(data);
                },
                error: (error: ErrorResponseModel): void => {
                    this.tableRows = [];
                    this.noMoreLogs = true;
                    this.logService.error('viewLogsComponent: getLogs()', error.message);
                }
            });
    }

    /**
     * Navigate backwards in the logs.
     */
    public goBackInLogs(): void {
        const options = this.getFormattedFormData();
        const startDateTime = this.firstLog;
        const startDateTimeOptions = this.getDateTime(startDateTime);

        const endDateTime = new Date(this.tableRows[this.tableRows.length - 1]['datetime']);
        endDateTime.setSeconds(endDateTime.getSeconds() - 1);
        const endDateTimeOptions = this.getDateTime(endDateTime);

        options.end = endDateTimeOptions['date'] + ' ' + endDateTimeOptions['time'];
        options.start = startDateTimeOptions['date'] + ' ' + startDateTimeOptions['time'];
        this.getLogs(options);
    }

    /**
     * Navigate forwards in the logs.
     */
    public goForwardInLogs(): void {
        const options = this.getFormattedFormData();

        options.start = this.tableRows[this.tableRows.length - 1]['datetime'];
        options.reverse = false;
        options.end = '';

        this.getLogs(options);
    }

    /**
     * Set available services
     * @param services
     * @private
     */
    private setAvailableServices(services: any[]): void {
        this.availableServices = services;
        this.setServiceIds();
    }

    /**
     * Set ids for the services in the view.
     * @private
     */
    private setServiceIds(): void {
        for (const service of this.availableServices) {
            service['id'] = service.nameFull.split(' ').join('-').toLowerCase();
        }
    }

    /**
     * Format the services.
     * @param services
     * @private
     */
    private formatServices(services: any[]): string[] {
        const filterSelected = services.filter(item => item.selected);

        return filterSelected.map(item => item.name);
    }

    /**
     * Set the disabled dates in the date picker.
     * @param data
     * @private
     */
    private setDatePickerDisabledDates(data: any): void {
        const { date: minDate } = this.utilsService.getDateTimeObjectFromIsoDateTime(data.first);
        this.minDate = minDate;
        const { date: maxDate } = this.utilsService.getDateTimeObjectFromIsoDateTime(data.last);
        this.maxDate = maxDate;
    }

    /**
     * Set up the form to store the log configuration data.
     * @param data
     * @private
     */
    private setUpForm(data: any): void {
        const dateObject = this.utilsService.getDateTimeObjectFromIsoDateTime(new Date().toISOString());
        this.firstLog = new Date(data.first);

        const { startTime, endTime } = this.getTimesFromData(data);

        this.logsForm = this.formBuilder.group({
            lines: data['logs'].length,
            startDate: dateObject.date,
            startTime: [startTime],
            endDate: data.end.split('T')[0],
            endTime: [endTime],
            reverse: [true],
            services: this.formBuilder.array(this.buildServiceFormGroups(this.availableServices))
        });
    }

    /**
     * Check log length before populating table rows.
     * @param data
     * @private
     */
    private checkLogLength(data: any): void {
        this.tableRows = [];
        if (data['logs']?.length === 0) {
            // If there is no begin log returned form the API, set not more logs to true and do not update tableRows.
            this.noMoreLogs = true;
            this.logService.error('viewLogsComponent: getLogs()', 'No more logs to show.');
        } else {
            // If there are logs, set noMoreLogs to false, clear out table rows and loop through logs to get tableRows.
            this.noMoreLogs = false;
            this.populateTableRows(data);
        }
    }

    /**
     * Populate table rows.
     * @param data
     * @private
     */
    private populateTableRows(data: any): void {
        for (const log of data['logs']) {
            this.tableRows.push({
                datetime: log.datetime,
                comm: log.comm,
                service: this.getFriendlyCommName(log.comm),
                gid: log.gid,
                hostname: log.hostname,
                message: log.message,
                pid: log.pid,
                uid: log.uid
            });
        }
    }

    /**
     * Get the times from the data.
     * @param data
     * @private
     */
    private getTimesFromData(data: any): any {
        const startTimeDate = data.start.split('T');
        const startTimeSplit = startTimeDate[1].split(':');
        const endTimeDate = data.end.split('T');
        const endTimeSplit = endTimeDate[1].split(':');

        return {
            startTime: startTimeSplit[0] + ':' + startTimeSplit[1],
            endTime: endTimeSplit[0] + ':' + endTimeSplit[1]
        };
    }

    /**
     * Get friendly command name to display to user in table.
     * @param comm
     * @private
     */
    private getFriendlyCommName(comm: any): string {
        const index = this.availableServices.findIndex(item => item.name === comm);

        if (index !== -1) {
            return this.availableServices[index].nameFull;
        }

        return '';
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        forkJoin({
            services: this.viewLogsApiService.getServices(),
            allLogs: this.viewLogsApiService.getAllLogs('100', { timeout: 30000 })
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                dataGroup => {
                    if (dataGroup['services']) {
                        this.setAvailableServices(dataGroup['services']);
                    }

                    if (dataGroup['allLogs']) {
                        const data = dataGroup['allLogs'];
                        this.setDatePickerDisabledDates(data);
                        this.setUpForm(data);
                        this.logsLoaded = true;
                        this.subscribeToFormChanges();
                        this.populateTableRows(data);
                    }
                },
                error => {
                    this.logService.error('LogsComponent:setUpPageData', 'Get page data failed' + error);
                    this.errorLoaded = true;
                }
            );
    }

    /**
     * Subscribe to the form changes and set the values to be validated.
     * @private
     */
    private subscribeToFormChanges(): void {
        this.logsForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: value => {
                    this.validateDateAndTimes(value.endDate, value.endTime);

                    this.activeServices = this.formatServices(value.services);
                    this.clearAllActive = this.activeServices && this.activeServices.length === 0;

                    this.enableDisableSelectAll();
                }
            });
    }

    /**
     * Set up the services form group.
     * @private
     */
    private buildServiceFormGroups(services: any[]): FormGroup[] {
        const formGroups = [];
        for (const service of services) {
            formGroups.push(this.formBuilder.group({
                fullName: service.nameFull,
                name: service.name,
                selected: true
            }));
        }

        return formGroups;
    }

    /**
     * Get the formatted date from the form.
     * @private
     */
    private getFormattedFormData(): any {
        const formData = this.logsForm.value;
        // Get the start day by getting the formatted end dateTime then take away 1 day from that.
        const endDateTime = new Date(this.formatDateTime(formData.endDate, formData.endTime));
        endDateTime.setDate(endDateTime.getDate() - 1);
        const { date, time } = this.getDateTime(endDateTime);

        return {
            services: this.formatServices(formData.services),
            reverse: formData.reverse,
            lines: formData.lines,
            start: date + ' ' + time,
            end: this.formatDateTime(formData.endDate, formData.endTime),
        };
    }

    /**
     * Format the date and times to the correct format from the API.
     * @param date
     * @param time
     * @private
     */
    private formatDateTime(date: string, time: any): string {
        const year = date.split('-')[0];
        const month = date.split('-')[1];
        const day = date.split('-')[2];

        return `${year}-${month}-${day} ${time}:00`;
    }

    /**
     * Enable or disable the select all button.
     * @private
     */
    private enableDisableSelectAll(): void {
        const active = this.activeServices;
        const available = this.availableServices;
        this.selectAllToggle = !((active && available) && (active.length < available.length));
    }

    /**
     * Add validation to the date and time inputs.
     * @param endDate
     * @param endTime
     * @private
     */
    private validateDateAndTimes(endDate: string, endTime: string): void {
        const dateTime = this.formatDateTime(endDate, endTime);
        const currentDateTime = new Date();

        const firstLogFormatted = this.getDateTime(this.firstLog);

        const dateTimeSplit = dateTime.split(' ');
        const currentDateTimeFormatted = this.getDateTime(currentDateTime);

        if (dateTimeSplit[0] === currentDateTimeFormatted.date) {
            // If the dates are the same check the times
            if (dateTimeSplit[1] > currentDateTimeFormatted.time) {
                // If the start time is greater than the current time show error message.
                this.logsForm.get('endTime').setErrors({
                    'timeValidation': `${this.languageService.translate('invalid_start_time')}`
                });
            } else {
                this.logsForm.get('endTime').setErrors(null);
            }
        }
        if (dateTimeSplit[0] === firstLogFormatted.date && dateTimeSplit[1] < firstLogFormatted.time) {
            // If the date is the same date as the first log and the time is before the time of the first log, show the error.
            this.logsForm.get('endTime').setErrors({
                'timeValidation': `${this.languageService.translate('no_logs_before')}${this.firstLog}`
            });
            this.logsForm.get('endTime').markAsTouched();
        }
    }

    /**
     * Get the date and time values from the string formatted dateTime from new Date().
     * @param dateTime
     * @private
     */
    private getDateTime(dateTime: any): any {
        const date = dateTime.toISOString().slice(0, 10);
        // Due to the timezone used in toISOString, the time returns an hour behind the current time. So add an hour then get the time.
        const timeOffSet = (new Date()).getTimezoneOffset() * 60000;
        const time = (new Date(dateTime - timeOffSet)).toISOString().slice(11, 19);

        return { date, time };
    }

    /**
     * Enables buttons in the lookup if the user has the correct access levels.
     * @private
     */
    private enableButtonsInTheLookUp(): void {
        // const buttonsArray = ['logs-update-logs', 'logs-forward', 'logs-back'];
        // this.pageLookUp = this.rbacService.getPageRequiredRoutes('view-logs');
        // this.rbacService.setButtonStateInLookup(this.pageLookUp, buttonsArray);
    }
}
